/**
 * Unified county name normalization for consistent matching across the application.
 *
 * This handles various county name formats across different data sources:
 * - Geocoding results: "Rock Island County", "St. Joseph County"
 * - T2M states with different formats:
 *   - IL: "Rock Island  " (no "County", trailing spaces)
 *   - IN: "St. Joseph" (no "County", can have periods)
 *   - WI: "Rock County(10062)" (has "County" suffix + code in parens)
 *   - KY: "Jefferson County" (has "County" suffix, sometimes trailing spaces)
 * - User input: may have various abbreviations like "Co.", "St.", "Saint"
 */

/**
 * Normalize a county name for comparison purposes.
 *
 * Normalization steps:
 * 1. Convert to lowercase
 * 2. Remove parenthetical content (e.g., "(10062)" in WI county names)
 * 3. Remove "county" or "co." suffixes
 * 4. Normalize "St." and "St" variations to "saint"
 * 5. Remove dashes (for hyphenated counties)
 * 6. Collapse multiple spaces to single space
 * 7. Trim whitespace
 *
 * @param county - The county name to normalize
 * @returns Normalized county name for comparison
 */
export function normalizeCountyName(county: string): string {
  return (
    county
      .toLowerCase()
      // Remove anything in parentheses (e.g., WI's "(10062)")
      .replace(/\s*\([^)]*\)/g, "")
      // Remove "county" suffix (must be preceded by space to avoid partial matches)
      .replace(/\s+county\s*$/gi, "")
      // Remove "co." abbreviation at end of string or followed by space/punctuation
      // Must be preceded by space to avoid matching "co" in words like "Cook"
      .replace(/\s+co\.?\s*$/gi, "")
      .replace(/\s+co\.\s+/gi, " ")
      // Normalize "St." and "St" (not followed by more letters) to "saint "
      .replace(/\bst\.?\s+/gi, "saint ")
      // Remove dashes (for hyphenated county names)
      .replace(/-/g, " ")
      // Remove single quotes (e.g., "Anne's")
      .replace(/'/g, "")
      // Remove all spaces for the purposes of matching
      .replace(/\s+/g, "")
      .trim()
  );
}

/**
 * Get all variations of a county name for matching purposes.
 *
 * This handles slash-separated county names (e.g., "Montgomery/Greene")
 * by splitting them into individual counties for matching.
 *
 * @param county - The county name to get variations for
 * @returns Array of normalized county name variations
 */
function getCountyVariations(county: string): string[] {
  const normalized = normalizeCountyName(county);
  const variations = [normalized];

  // If the county contains a slash, also include each part as a variation
  // This handles cases like "Montgomery/Greene" matching "Montgomery"
  if (county.includes("/")) {
    const parts = county.split("/").map((s) => normalizeCountyName(s.trim()));
    variations.push(...parts);
  }

  return [...new Set(variations)];
}

/**
 * Adds "County" to the end of a county name if not present,
 * else keeps the county designation (county, parish, borough, etc)
 */
const COUNTY_DESIGNATION_PATTERN =
  /\b(County|Parish|Borough|Census Area|Municipality|City and Borough)$/i;

export function formatCountyDisplay(value: string): string {
  if (COUNTY_DESIGNATION_PATTERN.test(value)) return value;
  return `${value} County`;
}

/**
 * Check if two county names match after normalization.
 *
 * Handles:
 * - Direct matches after normalization
 * - Slash-separated county names (e.g., "Montgomery" matches "Montgomery/Greene")
 *
 * @param county1 - First county name
 * @param county2 - Second county name
 * @returns True if the normalized names match
 */
export function countyNamesMatch(county1: string, county2: string): boolean {
  const normalized1 = normalizeCountyName(county1);
  const variations2 = getCountyVariations(county2);

  // Check if county1 matches any variation of county2
  if (variations2.includes(normalized1)) {
    return true;
  }

  // Also check if county2 matches any variation of county1 (symmetric matching)
  const variations1 = getCountyVariations(county1);
  return variations1.includes(normalizeCountyName(county2));
}

/**
 * Maps a facility state + county name to the jurisdiction key used in
 * requirement rules (e.g. "St. Clair" + AL → "AL_ST_CLAIR").
 *
 * Unlike normalizeCountyName (which produces a lowercase string for fuzzy
 * comparison), this function produces an uppercase STATE_COUNTY key for
 * exact lookup against predefined jurisdiction key sets.
 */
export function normalizeCountyToJurisdictionKey(
  state: string | null | undefined,
  county: string | null | undefined
): string | null {
  if (!state?.trim() || !county?.trim()) {
    return null;
  }

  let normalized = county.trim();
  // Remove parenthetical content (e.g. WI-style "(10062)")
  normalized = normalized.replace(/\s*\([^)]*\)/g, "");
  // Remove "County" / "Co." suffix
  normalized = normalized.replace(/\s+county\s*$/i, "").replace(/\s+co\.?\s*$/i, "");
  // Remove periods and single quotes
  normalized = normalized.replace(/[.']/g, "");
  // Normalize "Saint X" → "St X" so both spellings produce the same key
  normalized = normalized.replace(/\bsaint\b/gi, "st");
  // Replace spaces and dashes with underscores
  normalized = normalized.replace(/[\s-]+/g, "_");
  // Collapse repeated underscores and trim leading/trailing
  normalized = normalized.replace(/_+/g, "_").replace(/^_|_$/g, "");

  if (!normalized) {
    return null;
  }

  return `${state.trim().toUpperCase()}_${normalized.toUpperCase()}`;
}
